import type { ResultatsClassePayload } from '@/types/resultatsClasse'
import type { EtudiantsBisFetcher } from '@/utils/etudiantsBisPagination'
import {
  buildResultatsAnnuelsFilename,
  buildResultatsAnnuelsTable,
  formatPourcentageFr,
  type ResultatsAnnuelsLigne,
  type ResultatsAnnuelsTable,
} from '@/utils/resultatsAnnuelsHelpers'
import { loadEtudiantsBisForSessionSpecialite } from '@/utils/resultatsClasseHelpers'

/** Styles extraits du fichier source « Résultats Annuels Licence 2 BD Nov24.xlsx » */
const TEAL = 'FF134F5C'
const HEADER_FONT = 'FFF3F3F3'
const WHITE = 'FFFFFFFF'
const ZEBRA_ODD = 'FFF3F3F3'
const FILL_OK = 'FFD9EAD3'
const FILL_KO = 'FFF4CCCC'
const FILL_ADMIS = 'FF34A853' // theme accent4 Sheets
const FILL_CONDITIONNEL = 'FFFFD966'
const FILL_REDOUBLE = 'FFFF0000'
const FILL_NON_AUTORISE = 'FFCC4125'
const FONT_NAME = 'Arial'
const DATA_START_ROW = 4
const HEADER_ROW = 3
const TITLE_ROW = 1
const CREDITS_SEMESTRE_CIBLE = 30

const WHITE_MEDIUM = { style: 'medium' as const, color: { argb: WHITE } }
const WHITE_THICK = { style: 'thick' as const, color: { argb: WHITE } }

type ExcelJSWorkbook = {
  addWorksheet: (name: string, options?: Record<string, unknown>) => any
  xlsx: { writeBuffer: () => Promise<ArrayBuffer | Uint8Array> }
}

async function loadExcelJS(): Promise<{ Workbook: new () => ExcelJSWorkbook }> {
  let ExcelJSMod: any
  try {
    ExcelJSMod = await import('exceljs/dist/exceljs.min.js')
  } catch {
    ExcelJSMod = await import('exceljs')
  }
  return (ExcelJSMod as any)?.default || ExcelJSMod
}

function colLetter(index1: number): string {
  let n = index1
  let s = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    s = String.fromCharCode(65 + rem) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

function solidFill(argb: string) {
  return {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb },
    bgColor: { argb },
  }
}

function dataFont() {
  return {
    name: FONT_NAME,
    size: 10,
    bold: true,
    italic: false,
    color: { theme: 1 },
  }
}

function applyTitleStyle(cell: any) {
  cell.fill = solidFill(TEAL)
  cell.font = {
    name: FONT_NAME,
    size: 13,
    bold: true,
    italic: false,
    color: { argb: WHITE },
  }
  cell.alignment = { horizontal: 'center', vertical: 'middle' }
}

function applyHeaderStyle(cell: any, kind: 'first' | 'middle' | 'last', align: 'left' | 'center') {
  cell.fill = solidFill(TEAL)
  cell.font = {
    name: FONT_NAME,
    size: 13,
    bold: true,
    italic: false,
    color: { argb: HEADER_FONT },
  }
  cell.alignment = { horizontal: align, vertical: 'middle' }
  if (kind === 'first') {
    cell.border = { right: WHITE_MEDIUM }
  } else if (kind === 'last') {
    cell.border = { left: WHITE_THICK }
  } else {
    cell.border = { left: WHITE_MEDIUM, right: WHITE_MEDIUM }
  }
}

function zebraFill(rowIndex0: number) {
  return solidFill(rowIndex0 % 2 === 0 ? WHITE : ZEBRA_ODD)
}

function creditsFill(value: number | null | undefined, cible: number) {
  if (value == null || Number.isNaN(value)) return null
  if (value === cible) return solidFill(FILL_OK)
  if (value < cible) return solidFill(FILL_KO)
  return null
}

function decisionFill(decision: string) {
  const d = decision.trim().toLowerCase()
  if (d.includes('admis') && !d.includes('non')) return solidFill(FILL_ADMIS)
  if (d.includes('conditionnel')) return solidFill(FILL_CONDITIONNEL)
  if (d.includes('redouble')) return solidFill(FILL_REDOUBLE)
  if (d.includes('non autoris')) return solidFill(FILL_NON_AUTORISE)
  return null
}

function mentionFormula(cellRef: string): string {
  return `IF(${cellRef}<>"",IF(${cellRef}<=5,"Médiocre",IF(${cellRef}<10,"Insuffisant",IF(${cellRef}<12,"Passable",IF(${cellRef}<14,"Assez Bien",IF(${cellRef}<16,"Bien",IF(${cellRef}<18,"Très Bien",IF(${cellRef}>=18,"Excellent",""))))))),"")`
}

function downloadBuffer(buffer: ArrayBuffer | Uint8Array, filename: string) {
  const bytes =
    buffer instanceof Uint8Array
      ? Uint8Array.from(buffer)
      : new Uint8Array(buffer)
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function buildColumnLayout(table: ResultatsAnnuelsTable, anonymise: boolean) {
  let col = 1
  const matricule = col++
  const prenom = anonymise ? null : col++
  const nom = anonymise ? null : col++
  const semestres = table.semestreNumeros.map((numero) => ({
    numero,
    moyenne: col++,
    credits: col++,
    session: col++,
  }))
  const moyenneAn = col++
  const creditsTotaux = col++
  const mention = col++
  const decision = col++
  const session = col++
  const commentaires = col++
  return {
    matricule,
    prenom,
    nom,
    semestres,
    moyenneAn,
    creditsTotaux,
    mention,
    decision,
    session,
    commentaires,
    lastCol: commentaires,
  }
}

type ColumnLayout = ReturnType<typeof buildColumnLayout>

function applyBaseDataStyle(
  cell: any,
  rowIndex0: number,
  opts?: { align?: 'left' | 'center'; numFmt?: string; fillOverride?: ReturnType<typeof solidFill> | null }
) {
  cell.font = dataFont()
  cell.alignment = {
    horizontal: opts?.align,
    vertical: 'middle',
  }
  cell.fill = opts?.fillOverride ?? zebraFill(rowIndex0)
  cell.border = {}
  if (opts?.numFmt) cell.numFmt = opts.numFmt
}

function writeStudentRow(
  ws: any,
  rowIndex: number,
  rowIndex0: number,
  ligne: ResultatsAnnuelsLigne,
  layout: ColumnLayout,
  creditsSemestreCible: number,
  creditsTotauxCible: number
) {
  const row = ws.getRow(rowIndex)
  row.height = 21

  // Appliquer le fond zébré sur toute la largeur utile (comme Table_1 du modèle)
  for (let c = layout.matricule; c <= layout.lastCol; c++) {
    applyBaseDataStyle(row.getCell(c), rowIndex0)
  }

  const matriculeCell = row.getCell(layout.matricule)
  matriculeCell.value = ligne.matricule
  applyBaseDataStyle(matriculeCell, rowIndex0)

  if (layout.prenom != null) {
    const prenomCell = row.getCell(layout.prenom)
    prenomCell.value = ligne.prenom
    applyBaseDataStyle(prenomCell, rowIndex0)
  }
  if (layout.nom != null) {
    const nomCell = row.getCell(layout.nom)
    nomCell.value = ligne.nom
    applyBaseDataStyle(nomCell, rowIndex0, { align: 'left' })
  }

  const moyLetters: string[] = []
  const credLetters: string[] = []
  layout.semestres.forEach((sem, idx) => {
    const data = ligne.semestres[idx]
    const moyCell = row.getCell(sem.moyenne)
    if (data?.moyenne != null) moyCell.value = data.moyenne
    applyBaseDataStyle(moyCell, rowIndex0, { align: 'center', numFmt: '#,##0.00' })

    const credCell = row.getCell(sem.credits)
    const credits = data?.credits ?? 0
    credCell.value = credits
    applyBaseDataStyle(credCell, rowIndex0, {
      align: 'center',
      fillOverride: creditsFill(credits, creditsSemestreCible),
    })

    const sessionSemCell = row.getCell(sem.session)
    sessionSemCell.value = data?.session ?? '-'
    applyBaseDataStyle(sessionSemCell, rowIndex0, { align: 'center' })

    moyLetters.push(`${colLetter(sem.moyenne)}${rowIndex}`)
    credLetters.push(`${colLetter(sem.credits)}${rowIndex}`)
  })

  const moyAnCell = row.getCell(layout.moyenneAn)
  moyAnCell.value = {
    formula:
      moyLetters.length === 2
        ? `(${moyLetters[0]}+${moyLetters[1]})/2`
        : `(${moyLetters.join('+')})/${Math.max(moyLetters.length, 1)}`,
  }
  applyBaseDataStyle(moyAnCell, rowIndex0, { align: 'center', numFmt: '#,##0.00' })

  const totalCell = row.getCell(layout.creditsTotaux)
  totalCell.value = {
    formula: credLetters.length > 0 ? credLetters.join('+') : '0',
  }
  applyBaseDataStyle(totalCell, rowIndex0, {
    align: 'center',
    fillOverride: creditsFill(ligne.creditsTotaux, creditsTotauxCible),
  })

  const mentionCell = row.getCell(layout.mention)
  mentionCell.value = { formula: mentionFormula(`${colLetter(layout.moyenneAn)}${rowIndex}`) }
  applyBaseDataStyle(mentionCell, rowIndex0, { align: 'center' })

  const decisionCell = row.getCell(layout.decision)
  decisionCell.value = ligne.decision
  applyBaseDataStyle(decisionCell, rowIndex0, {
    align: 'center',
    fillOverride: decisionFill(ligne.decision),
  })

  const sessionCell = row.getCell(layout.session)
  sessionCell.value = ligne.session
  applyBaseDataStyle(sessionCell, rowIndex0, { align: 'center' })

  const commentsCell = row.getCell(layout.commentaires)
  commentsCell.value = ligne.commentaires || null
  applyBaseDataStyle(commentsCell, rowIndex0, { align: 'center' })
}

function addConditionalFormatting(
  ws: any,
  layout: ColumnLayout,
  firstDataRow: number,
  lastDataRow: number,
  creditsSemestreCible: number,
  creditsTotauxCible: number
) {
  if (lastDataRow < firstDataRow) return

  const creditsRefs = layout.semestres
    .map((s) => `${colLetter(s.credits)}${firstDataRow}:${colLetter(s.credits)}${lastDataRow}`)
    .join(' ')
  const totalRef = `${colLetter(layout.creditsTotaux)}${firstDataRow}:${colLetter(layout.creditsTotaux)}${lastDataRow}`
  const decisionRef = `${colLetter(layout.decision)}${firstDataRow}:${colLetter(layout.decision)}${lastDataRow}`
  const decisionFirst = `${colLetter(layout.decision)}${firstDataRow}`

  if (creditsRefs) {
    ws.addConditionalFormatting({
      ref: creditsRefs,
      rules: [
        {
          type: 'cellIs',
          operator: 'equal',
          formulae: [String(creditsSemestreCible)],
          priority: 1,
          style: { fill: solidFill(FILL_OK) },
        },
      ],
    })
    ws.addConditionalFormatting({
      ref: creditsRefs,
      rules: [
        {
          type: 'cellIs',
          operator: 'lessThan',
          formulae: [String(creditsSemestreCible)],
          priority: 2,
          style: { fill: solidFill(FILL_KO) },
        },
      ],
    })
  }

  ws.addConditionalFormatting({
    ref: totalRef,
    rules: [
      {
        type: 'cellIs',
        operator: 'equal',
        formulae: [String(creditsTotauxCible)],
        priority: 3,
        style: { fill: solidFill(FILL_OK) },
      },
    ],
  })
  ws.addConditionalFormatting({
    ref: totalRef,
    rules: [
      {
        type: 'cellIs',
        operator: 'lessThan',
        formulae: [String(creditsTotauxCible)],
        priority: 4,
        style: { fill: solidFill(FILL_KO) },
      },
    ],
  })

  ws.addConditionalFormatting({
    ref: decisionRef,
    rules: [
      {
        type: 'containsText',
        operator: 'containsText',
        text: 'Admis',
        priority: 5,
        formulae: [`NOT(ISERROR(SEARCH(("Admis"),(${decisionFirst}))))`],
        style: { fill: solidFill(FILL_ADMIS) },
      },
    ],
  })
  ws.addConditionalFormatting({
    ref: decisionRef,
    rules: [
      {
        type: 'containsText',
        operator: 'containsText',
        text: 'Passage conditionnel',
        priority: 6,
        formulae: [`NOT(ISERROR(SEARCH(("Passage conditionnel"),(${decisionFirst}))))`],
        style: { fill: solidFill(FILL_CONDITIONNEL) },
      },
    ],
  })
  ws.addConditionalFormatting({
    ref: decisionRef,
    rules: [
      {
        type: 'containsText',
        operator: 'containsText',
        text: 'Redouble',
        priority: 7,
        formulae: [`NOT(ISERROR(SEARCH(("Redouble"),(${decisionFirst}))))`],
        style: { fill: solidFill(FILL_REDOUBLE) },
      },
    ],
  })
}

function writeWorkbook(
  ExcelJS: { Workbook: new () => ExcelJSWorkbook },
  table: ResultatsAnnuelsTable,
  anonymise: boolean
) {
  const workbook = new ExcelJS.Workbook()
  const ws = workbook.addWorksheet(table.sheetName || 'Resultats', {
    properties: {
      defaultColWidth: 12.63,
      defaultRowHeight: 15.75,
    },
    views: [
      {
        state: 'frozen',
        xSplit: anonymise ? 1 : 3,
        ySplit: 0,
        topLeftCell: anonymise ? 'B1' : 'D1',
      },
    ],
  })
  const layout = buildColumnLayout(table, anonymise)

  // Largeurs exactes du fichier source (ExcelJS)
  ws.getColumn(layout.matricule).width = 11.38
  if (layout.prenom != null) ws.getColumn(layout.prenom).width = 25.25
  if (layout.nom != null) ws.getColumn(layout.nom).width = 20.13
  layout.semestres.forEach((sem) => {
    ws.getColumn(sem.moyenne).width = 14.5
    ws.getColumn(sem.credits).width = 14.5
    ws.getColumn(sem.session).width = 16.13
  })
  ws.getColumn(layout.moyenneAn).width = 14.5
  ws.getColumn(layout.creditsTotaux).width = 14.5
  ws.getColumn(layout.mention).width = 14.5
  ws.getColumn(layout.decision).width = 19.75
  ws.getColumn(layout.session).width = 16.13
  ws.getColumn(layout.commentaires).width = 17.13

  ws.getRow(TITLE_ROW).height = 30
  ws.getRow(2).height = 30
  ws.getRow(HEADER_ROW).height = 30

  const titleStart = layout.semestres[0]?.moyenne ?? layout.moyenneAn
  const titleEnd = layout.session
  ws.mergeCells(TITLE_ROW, titleStart, TITLE_ROW, titleEnd)
  const titleCell = ws.getCell(TITLE_ROW, titleStart)
  titleCell.value = table.title
  applyTitleStyle(titleCell)

  const headerRow = ws.getRow(HEADER_ROW)
  const headers: Array<{ col: number; text: string; align: 'left' | 'center' }> = [
    { col: layout.matricule, text: 'Matricule', align: 'left' },
  ]
  if (layout.prenom != null) headers.push({ col: layout.prenom, text: 'Prénoms', align: 'left' })
  if (layout.nom != null) headers.push({ col: layout.nom, text: 'Nom', align: 'left' })
  layout.semestres.forEach((sem) => {
    headers.push({ col: sem.moyenne, text: `Moy. S${sem.numero}`, align: 'center' })
    headers.push({ col: sem.credits, text: `Crédits S${sem.numero}`, align: 'center' })
    headers.push({ col: sem.session, text: `Session S${sem.numero}`, align: 'center' })
  })
  headers.push(
    { col: layout.moyenneAn, text: 'Moy. An.', align: 'center' },
    { col: layout.creditsTotaux, text: 'Total Crédits', align: 'center' },
    { col: layout.mention, text: 'Mention', align: 'center' },
    { col: layout.decision, text: 'Décision du jury', align: 'center' },
    { col: layout.session, text: 'Session', align: 'center' },
    { col: layout.commentaires, text: 'Commentaires', align: 'center' }
  )
  headers.forEach(({ col, text, align }, idx) => {
    const cell = headerRow.getCell(col)
    cell.value = text
    let kind: 'first' | 'middle' | 'last' = 'middle'
    if (idx === 0) kind = 'first'
    else if (col >= layout.decision) kind = 'last'
    applyHeaderStyle(cell, kind, align)
  })

  const creditsSemestreCible = CREDITS_SEMESTRE_CIBLE
  const creditsTotauxCible =
    table.seuils.passage_superieur_min || creditsSemestreCible * Math.max(table.semestreNumeros.length, 1)

  table.lignes.forEach((ligne, idx) => {
    writeStudentRow(
      ws,
      DATA_START_ROW + idx,
      idx,
      ligne,
      layout,
      creditsSemestreCible,
      creditsTotauxCible
    )
  })

  const lastDataRow =
    table.lignes.length > 0 ? DATA_START_ROW + table.lignes.length - 1 : HEADER_ROW

  addConditionalFormatting(
    ws,
    layout,
    DATA_START_ROW,
    lastDataRow,
    creditsSemestreCible,
    creditsTotauxCible
  )

  // Stats : colonne « Crédits Sx » du 1er semestre (= E dans le modèle), une ligne sur deux
  const statsCol = layout.semestres[0]?.credits ?? layout.matricule
  const total = table.effectifs.total || table.lignes.length
  const stats = [
    { label: 'Admis', count: table.effectifs.passage_superieur },
    { label: 'Redouble', count: table.effectifs.redouble },
    { label: 'Passage conditionnel', count: table.effectifs.admis_avec_rattrapage },
    { label: 'Passage non autorisé', count: 0 },
  ]
  const summaryRow = lastDataRow + 2
  stats.forEach((stat, idx) => {
    const rowIndex = summaryRow + idx * 2
    const row = ws.getRow(rowIndex)
    row.height = 30
    ws.getRow(rowIndex - 1).height = 30
    const cell = row.getCell(statsCol)
    cell.value = `${stat.label} : ${formatPourcentageFr(stat.count, total)}`
    cell.font = dataFont()
  })

  // Ligne récap (comme H44 / K44 / L44 du fichier source)
  if (table.lignes.length > 0) {
    const dataRange = (col: number) =>
      `${colLetter(col)}${DATA_START_ROW}:${colLetter(col)}${lastDataRow}`
    const summary = ws.getRow(summaryRow)
    summary.height = 30

    const moyenneCell = summary.getCell(layout.moyenneAn)
    moyenneCell.value = { formula: `AVERAGE(${dataRange(layout.moyenneAn)})` }
    moyenneCell.font = dataFont()
    moyenneCell.numFmt = '#,##0.00'
    moyenneCell.alignment = { horizontal: 'center', vertical: 'middle' }

    const admisCell = summary.getCell(layout.decision)
    admisCell.value = { formula: `COUNTIF(${dataRange(layout.decision)},"Admis")` }
    admisCell.font = { name: FONT_NAME, size: 10, bold: false, italic: false, color: { theme: 1 } }
    admisCell.alignment = { horizontal: 'center', vertical: 'middle' }

    const sessionNormaleCell = summary.getCell(layout.session)
    sessionNormaleCell.value = {
      formula: `COUNTIF(${dataRange(layout.session)},"Normale")`,
    }
    sessionNormaleCell.font = {
      name: FONT_NAME,
      size: 10,
      bold: false,
      italic: false,
      color: { theme: 1 },
    }
    sessionNormaleCell.alignment = { horizontal: 'center', vertical: 'middle' }
  }

  return workbook
}

export async function generateResultatsAnnuelsExcel(
  payload: ResultatsClassePayload,
  options: {
    anonymise?: boolean
    fetchEtudiantsBis: EtudiantsBisFetcher
  }
): Promise<void> {
  const anonymise = Boolean(options.anonymise)
  const etudiantsBis = await loadEtudiantsBisForSessionSpecialite(
    options.fetchEtudiantsBis,
    payload.session.id,
    payload.specialite.id
  )
  const table = buildResultatsAnnuelsTable(payload, etudiantsBis, { anonymise })
  const ExcelJS = await loadExcelJS()
  const workbook = writeWorkbook(ExcelJS, table, anonymise)
  const buffer = await workbook.xlsx.writeBuffer()
  downloadBuffer(buffer, buildResultatsAnnuelsFilename(payload, anonymise))
}
